import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../services/notification.service';
import { IngredientAssociationService } from '../../services/ingredient-association.service';
import * as AssocActions from './ingredient-association.actions';

export const loadIngredientAssociations$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AssocActions.loadIngredientAssociations),
      mergeMap(({ fromId }) =>
        assocService.getAssociations(fromId).pipe(
          map(associations =>
            AssocActions.loadIngredientAssociationsSuccess({ associations })
          ),
          catchError(error => {
            notification.error(
              translate.instant('ingredientAssoc.notifications.loadFailure')
            );
            return of(
              AssocActions.loadIngredientAssociationsFailure({ error })
            );
          })
        )
      )
    ),
  { functional: true }
);

export const addIngredientAssociation$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AssocActions.addIngredientAssociation),
      mergeMap(({ from_ingredient_id, to_ingredient_id, strength, ratio, note }) =>
        assocService.addAssociation({
          from_ingredient_id,
          to_ingredient_id,
          strength,
          ratio,
          note: note ?? null
        }).pipe(
          map(association => {
            notification.success(
              translate.instant('ingredientAssoc.notifications.addSuccess')
            );
            return AssocActions.addIngredientAssociationSuccess({ association });
          }),
          catchError(error => {
            notification.error(
              translate.instant('ingredientAssoc.notifications.addFailure')
            );
            return of(
              AssocActions.addIngredientAssociationFailure({ error })
            );
          })
        )
      )
    ),
  { functional: true }
);

export const updateIngredientAssociation$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AssocActions.updateIngredientAssociation),
      mergeMap(({ association_id, changes }) =>
        assocService.updateAssociation({
          association_id,
          ...changes
        }).pipe(
          map(association => {
            notification.success(
              translate.instant('ingredientAssoc.notifications.updateSuccess')
            );
            return AssocActions.updateIngredientAssociationSuccess({ association });
          }),
          catchError(error => {
            notification.error(
              translate.instant('ingredientAssoc.notifications.updateFailure')
            );
            return of(
              AssocActions.updateIngredientAssociationFailure({ error })
            );
          })
        )
      )
    ),
  { functional: true }
);

export const deleteIngredientAssociation$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AssocActions.deleteIngredientAssociation),
      mergeMap(({ association_id }) =>
        assocService.deleteAssociation(association_id).pipe(
          map(() => {
            notification.success(
              translate.instant('ingredientAssoc.notifications.deleteSuccess')
            );
            return AssocActions.deleteIngredientAssociationSuccess({ association_id });
          }),
          catchError(error => {
            notification.error(
              translate.instant('ingredientAssoc.notifications.deleteFailure')
            );
            return of(
              AssocActions.deleteIngredientAssociationFailure({ error })
            );
          })
        )
      )
    ),
  { functional: true }
);

export const generateIngredientAssociationsWithAi$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) => actions$.pipe(
    ofType(AssocActions.generateIngredientAssociationsWithAi),
    exhaustMap(({ ingredient_id, language }) => assocService.generateAssociationsWithAi(ingredient_id, language).pipe(
      map(result => AssocActions.generateIngredientAssociationsWithAiSuccess(result)),
      catchError(error => {
        notification.error(translate.instant('ingredientAssoc.ai.notifications.generateFailure'));
        return of(AssocActions.generateIngredientAssociationsWithAiFailure({ error }));
      })
    ))
  ),
  { functional: true }
);

export const applySelectedIngredientAssociations$ = createEffect(
  (
    actions$ = inject(Actions),
    assocService = inject(IngredientAssociationService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) => actions$.pipe(
    ofType(AssocActions.applySelectedIngredientAssociations),
    exhaustMap(({ associations }) => assocService.applyAiSuggestions(associations).pipe(
      map(result => {
        const key = result.skipped.length > 0
          ? 'ingredientAssoc.ai.notifications.applyPartial'
          : 'ingredientAssoc.ai.notifications.applySuccess';
        notification.success(translate.instant(key, {
          created: result.created.length,
          skipped: result.skipped.length
        }));
        return AssocActions.applySelectedIngredientAssociationsSuccess({ result });
      }),
      catchError(error => {
        notification.error(translate.instant('ingredientAssoc.ai.notifications.applyFailure'));
        return of(AssocActions.applySelectedIngredientAssociationsFailure({ error }));
      })
    ))
  ),
  { functional: true }
);

export const IngredientAssociationEffects = {
  loadIngredientAssociations$,
  addIngredientAssociation$,
  updateIngredientAssociation$,
  deleteIngredientAssociation$,
  generateIngredientAssociationsWithAi$,
  applySelectedIngredientAssociations$
};
